// Converted from shared/design/components/TopBar.html by scripts/dc2tsx; edited by hand from here on.
import React, { Fragment } from 'react';
import { DCLogic } from '../ds/DCLogic';
import { each, ip, route } from '../ds/tpl';
import { t, tf } from '../i18n';
import { unit, age } from '../lib/format';
import { alertMessage } from '../lib/names';
import { rich } from '../lib/rich';

export interface TopBarProps {
  collector?: 'ok' | 'warn' | 'crit' | (string & {});
  licence?: 'none' | 'expiring' | 'expired' | 'readonly' | 'console' | (string & {});
  alerts?: number;
  role?: 'owner' | 'risk' | 'dealer' | 'launcher' | (string & {});
  searchOpen?: boolean;
  /** design demo: the search opens with this query (a pasted client token, e.g. `ACC-7F3K2QZMXW4D`) */
  searchQuery?: '' | 'ACC-7F3K2QZMXW4D' | 'IP-9H4C2R7TN5WX' | 'ACC-Q8M2V6TR1ZK9' | 'XAU' | (string & {});
  drawerOpen?: boolean;
  menuOpen?: boolean;
  theme?: any;
  dir?: any;
  onTheme?: any;
  onDir?: any;
  demo?: any;
  /** live mode (app shell): GET /shell server block, merged with the `shell` topic */
  server?: {
    name: string;
    status: 'ok' | 'warn' | 'idle' | 'crit' | 'unknown';
    connected: boolean;
    latency_ms: number | null;
    ticks_per_second: number;
    last_tick_at: string | null;
    collector_location: string;
    collector_status: string;
  };
  /** signed-in user for the avatar and menu */
  user?: { name: string; email: string; role: string };
  /** open and critical alert counts */
  counts?: { alerts_open: number; alerts_critical: number };
  /** licence banner from the API (LicenceBanner) with its dates */
  licenceBanner?: {
    level: 'warn' | 'crit';
    kind: string;
    days_left: number | null;
    admin_only?: boolean;
    date?: string | null;
  } | null;
  /** active languages and the current one */
  languages?: { code: string; native_name: string; direction: string }[];
  lang?: string;
  onLang?: (code: string) => void;
  sound?: boolean;
  onSound?: (on: boolean) => void;
  /**
   * search provider: GET /search grouped results, or `{ groups, token }` when the query was a client
   * token (`token.kind` account / email / phone / ip, `token.found` false → "No account you can see has this token.")
   */
  onSearch?: (q: string) => Promise<TopBarSearchGroup[] | { groups: TopBarSearchGroup[]; token?: TopBarSearchToken | null }>;
  /** latest alerts for the drawer (Alert objects) */
  alertItems?: {
    id: string;
    severity: string;
    title: string;
    message: string;
    fired_at: string;
    status: string;
    /** an old event drained from a buffer: nobody was paged */
    late?: boolean;
  }[];
  onAckAll?: (ids: string[]) => void;
  onNavigate?: (path: string) => void;
  onSignOut?: () => void;
  /** live-data paused banner (collector disconnected) */
  stale?: boolean;
}

/** one group of ⌘K results (live mode); account items of a token search have `kind: 'account'` */
export interface TopBarSearchGroup {
  label: string;
  kind: string;
  items: {
    id: string;
    label: string;
    sublabel?: string;
    path: string;
    badge?: string;
    badgeCls?: string;
  }[];
}

/** a pasted client token (GET /search `token`): its kind and whether it opened an account the user may see */
export interface TopBarSearchToken {
  kind: 'account' | 'email' | 'phone' | 'ip' | (string & {});
  found: boolean;
}

/** Client tokens as Ask Brainux and connected AI apps (MCP) show them: ACC-, EML-, PHN-, IP- + 12 characters. */
const TOKEN_RE = /^(ACC|EML|PHN|IP)-[0-9A-Z]{12}$/;

const SEARCH_ICONS: Record<string, string> = {
  account: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8',
  symbol: 'M4 19 9 13l4 3 7-9M15 7h5v5',
  group: 'M3 7h6l2 2h10v10H3z',
  case: 'M4 4h16v16H4zM8 9h8M8 13h8M8 17h5',
};

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : p[0]?.[1] || '')).toUpperCase();
}

export class TopBar extends DCLogic<TopBarProps> {
  constructor(props) {
    super(props);
    this.state = {
      t: 0,
      searchOpen: !!props.searchOpen || !!props.searchQuery,
      drawerOpen: !!props.drawerOpen,
      menuOpen: !!props.menuOpen,
      q: props.searchQuery || '',
      opening: false,
      dFilter: 'all',
      sound: true,
      paused: 14,
      theme: null,
      dir: null,
    };
    this.qRef = React.createRef<any>();
  }
  // Design sample: client tokens (ACC-, EML-, PHN-, IP- + 12 characters) as Ask Brainux and connected AI apps (MCP) show them.
  // A token resolves only to accounts inside the signed-in user's data scope; anything else reads "No account you can see has this token."
  tokenHits(tok?: any) {
    const row = (primary?: any, name?: any, secondary?: any, score?: any, sev?: any) => ({
      primary,
      name,
      secondary,
      badge: String(score),
      badgeCls: 'bx-badge bx-badge--' + sev + ' bx-badge--mono',
      href: 'TRD-02_account-overview.html',
      mono: true,
    });
    const T: any = {
      'ACC-7F3K2QZMXW4D': {
        kind: 'Login token',
        rows: [row('5014382', 'Karim Haddad', 'real\\std-a · A-Book · Latency arbitrage', 96, 'crit')],
      },
      'EML-2HW8QK4ZT9MB': {
        kind: 'Email token',
        rows: [row('5014382', 'Karim Haddad', 'Matched by email · real\\std-a · A-Book', 96, 'crit')],
      },
      'PHN-6RJ3XN9C1VQP': {
        kind: 'Phone token',
        rows: [row('5014382', 'Karim Haddad', 'Matched by phone number · real\\std-a · A-Book', 96, 'crit')],
      },
      'IP-T6C1XR8J3N5P': {
        kind: 'IP token · 185.214.36.18 · du, residential',
        rows: [row('5014382', 'Karim Haddad', 'real\\std-a · A-Book · Latency arbitrage', 96, 'crit')],
      },
      'IP-9H4C2R7TN5WX': {
        kind: 'IP token · 91.108.4.77 · Leaseweb, datacenter',
        rows: [
          row('5014382', 'Karim Haddad', 'real\\std-a · A-Book · group G-07', 96, 'crit'),
          row('5033107', 'Viktor Sokolov', 'real\\std-a · A-Book · group G-07', 93, 'crit'),
          row('5021904', 'Lucas Moreau', 'real\\std-b · B-Book · group G-07', 91, 'crit'),
          row('5030662', 'Tomasz Nowak', 'real\\std-b · B-Book · group G-07', 79, 'warn'),
          row('5018845', 'Mei Lin', 'real\\std-b · B-Book · group G-07', 68, 'warn'),
        ],
      },
    };
    return T[tok] || null;
  }
  isToken(s?: any) {
    return TOKEN_RE.test(s);
  }
  componentDidMount() {
    this.iv = setInterval(() => this.setState({ t: this.state.t + 1, paused: this.state.paused + 1 }), 1000);
    this.onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        clearTimeout(this._open);
        this.setState({ searchOpen: true, menuOpen: false, q: '', sel: 0, opening: false });
      }
    };
    window.addEventListener('keydown', this.onKey);
    if (this.props.server && this.props.searchQuery) this.runSearch(this.props.searchQuery, { now: true });
  }
  /**
   * Live search (debounced; `now` for a paste). A pasted token that resolves to exactly one account
   * shows "Opening …" and opens it.
   */
  runSearch(q: string, opts: { now?: boolean; paste?: boolean } = {}) {
    clearTimeout(this._open);
    this.setState({ q, sel: 0, opening: false });
    if (!this.props.onSearch) return;
    clearTimeout(this.sq);
    const n = (this.sn = (this.sn || 0) + 1);
    this.sq = setTimeout(
      async () => {
        try {
          const r = await this.props.onSearch!(q);
          if (n !== this.sn) return;
          const groups: TopBarSearchGroup[] = Array.isArray(r) ? r : r?.groups || [];
          const token: TopBarSearchToken | null = Array.isArray(r) ? null : r?.token || null;
          this.setState({ results: groups, token, resQ: q });
          const acc = token?.found ? groups.filter((g) => g.kind === 'account').flatMap((g) => g.items) : [];
          if (opts.paste && acc.length === 1) {
            this.setState({ opening: true });
            this._open = setTimeout(() => this.go(acc[0].path), 700);
          }
        } catch {
          if (n === this.sn) this.setState({ results: [], token: null, resQ: q });
        }
      },
      q && !opts.now ? 160 : 0,
    );
  }
  go(path: string) {
    clearTimeout(this._open);
    this.setState({ searchOpen: false, drawerOpen: false, menuOpen: false, opening: false });
    if (this.props.onNavigate) this.props.onNavigate(path);
  }
  componentDidUpdate(prev?: any) {
    if (prev && (prev.searchOpen !== this.props.searchOpen || prev.searchQuery !== this.props.searchQuery)) {
      clearTimeout(this._open);
      this.setState({ searchOpen: !!this.props.searchOpen || !!this.props.searchQuery, q: this.props.searchQuery || '', opening: false });
      if (this.props.server && this.props.searchQuery) this.runSearch(this.props.searchQuery, { now: true });
    }
    if (prev && prev.drawerOpen !== this.props.drawerOpen) this.setState({ drawerOpen: !!this.props.drawerOpen });
    if (prev && prev.menuOpen !== this.props.menuOpen) this.setState({ menuOpen: !!this.props.menuOpen });
    if (this.state.searchOpen && this.qRef.current && !this._focused) {
      this._focused = true;
      try {
        this.qRef.current.focus();
      } catch (e) {}
    }
    if (!this.state.searchOpen) this._focused = false;
  }
  componentWillUnmount() {
    clearInterval(this.iv);
    clearTimeout(this.sq);
    clearTimeout(this._open);
    window.removeEventListener('keydown', this.onKey);
  }
  renderVals() {
    const tick = this.state.t;
    const collector = this.props.collector ?? 'ok';
    const role = this.props.role ?? 'risk';
    const theme = this.props.theme ?? this.state.theme ?? 'dark';
    const dir = this.props.dir ?? this.state.dir ?? 'ltr';
    const lat = 11 + ((tick * 7) % 4);
    const tps = 1780 + ((tick * 53) % 140);
    const pill = {
      ok: {
        cls: 'bx-pill',
        dot: 'bx-live bx-live--lg',
        sub: 'Collector LD4',
        subC: 'var(--muted)',
        metric: lat + ' ms · ' + tps.toLocaleString('en-US') + ' t/s',
        metC: 'var(--muted)',
      },
      warn: {
        cls: 'bx-pill bx-pill--warn',
        dot: 'bx-live bx-live--lg bx-live--warn',
        sub: 'Collector LD4 · high latency',
        subC: 'var(--warn)',
        metric: 138 + ((tick * 13) % 30) + ' ms · ' + (1210 + ((tick * 41) % 90)).toLocaleString('en-US') + ' t/s',
        metC: 'var(--warn)',
      },
      crit: {
        cls: 'bx-pill bx-pill--crit',
        dot: 'bx-live bx-live--lg bx-live--crit',
        sub: 'Disconnected',
        subC: 'var(--crit-text)',
        metric: 'last tick ' + this.state.paused + 's',
        metC: 'var(--crit-text)',
      },
    }[collector];
    const licMap = {
      expiring: {
        cls: 'bx-banner bx-banner--warn',
        title: 'Licence expires in 5 days.',
        text: 'Renew by 28 Oct 2026 to keep live monitoring, alerts and scheduled reports running.',
        cta: 'Enter licence key',
        btnCls: 'bx-btn bx-btn--sm',
        icon: 'M12 4 2.5 20h19L12 4ZM12 10v4M12 17.5v.01',
      },
      expired: {
        cls: 'bx-banner bx-banner--crit',
        title: 'Licence expired · read-only in 3 days.',
        text: 'On 27 Oct 2026 actions, alerts and scheduled reports stop. Live monitoring stays on until then.',
        cta: 'Renew licence',
        btnCls: 'bx-btn bx-btn--sm bx-btn--danger',
        icon: 'M12 4 2.5 20h19L12 4ZM12 10v4M12 17.5v.01',
      },
      readonly: {
        cls: 'bx-banner bx-banner--crit',
        title: 'Read-only mode.',
        text: 'The licence expired and its 7-day grace period has ended. Live data still shows; trading-server actions, alerts and scheduled reports are paused.',
        cta: 'Renew licence',
        btnCls: 'bx-btn bx-btn--sm bx-btn--danger',
        icon: 'M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5zM12 15v2',
      },
      console: {
        cls: 'bx-banner bx-banner--warn',
        title: 'Dux FX console unreachable for 7 days.',
        text: 'Licence checks are running on the grace period. Allow outbound HTTPS to console.duxfx.com from this instance. Only admins see this.',
        cta: 'View instance status',
        btnCls: 'bx-btn bx-btn--sm',
        icon: 'M12 4 2.5 20h19L12 4ZM12 10v4M12 17.5v.01',
      },
    };
    const licKey = this.props.licence ?? 'none';
    const lic = licMap[licKey] ? Object.assign({ show: true }, licMap[licKey]) : { show: false };

    const icons = {
      account: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8',
      symbol: 'M4 19 9 13l4 3 7-9M15 7h5v5',
      group: 'M3 7h6l2 2h10v10H3z',
      kase: 'M4 4h16v16H4zM8 9h8M8 13h8M8 17h5',
    };
    const all = [
      {
        g: 'Accounts',
        icon: icons.account,
        items: [
          {
            primary: '5014382',
            name: 'Karim Haddad',
            secondary: 'real\\std-a · A-Book · Latency arbitrage',
            badge: '96',
            badgeCls: 'bx-badge bx-badge--crit bx-badge--mono',
            href: 'TRD-02_account-overview.html',
            mono: true,
          },
          {
            primary: '5033107',
            name: 'Viktor Sokolov',
            secondary: 'real\\std-a · A-Book · Latency arbitrage',
            badge: '93',
            badgeCls: 'bx-badge bx-badge--crit bx-badge--mono',
            href: 'TRD-02_account-overview.html',
            mono: true,
          },
          {
            primary: '5041288',
            name: 'Ahmed Al-Rashid',
            secondary: 'real\\raw-b · B-Book · News trading',
            badge: '88',
            badgeCls: 'bx-badge bx-badge--crit bx-badge--mono',
            href: 'TRD-02_account-overview.html',
            mono: true,
          },
        ],
      },
      {
        g: 'Symbols',
        icon: icons.symbol,
        items: [
          {
            primary: 'XAUUSD',
            name: 'Gold vs US dollar',
            secondary: 'Net short 84.2 lots · 80% of limit · suffixes XAUUSD.r, XAUUSD.raw',
            badge: '80%',
            badgeCls: 'bx-badge bx-badge--warn bx-badge--mono',
            href: 'EXP-02_exposure-symbol.html',
            mono: true,
          },
          {
            primary: 'XAGUSD',
            name: 'Silver vs US dollar',
            secondary: 'Net long 9.6 lots · no limit set',
            badge: '',
            href: 'EXP-02_exposure-symbol.html',
            mono: true,
          },
          {
            primary: 'EURUSD',
            name: 'Euro vs US dollar',
            secondary: 'Net long 61.5 lots · 51% of limit',
            badge: '',
            href: 'EXP-02_exposure-symbol.html',
            mono: true,
          },
        ],
      },
      {
        g: 'Groups',
        icon: icons.group,
        items: [
          {
            primary: 'real\\std-b',
            name: 'Standard · B-Book',
            secondary: '1,912 accounts · 412 active this month',
            badge: '',
            href: 'ACC-04_group-detail.html',
            mono: true,
          },
          {
            primary: 'real\\std-a',
            name: 'Standard · A-Book',
            secondary: '640 accounts · 205 active this month',
            badge: '',
            href: 'ACC-04_group-detail.html',
            mono: true,
          },
        ],
      },
      {
        g: 'Cases',
        icon: icons.kase,
        items: [
          {
            primary: 'C-1042',
            name: '5014382 · Latency arbitrage',
            secondary: 'In review · Rana Malik · opened 22 Oct',
            badge: 'In review',
            badgeCls: 'bx-badge bx-badge--warn',
            href: 'CAS-02_case-detail.html',
            mono: true,
          },
          {
            primary: 'C-1041',
            name: 'Coordinated group G-07',
            secondary: 'Open · 5 accounts · opened 21 Oct',
            badge: 'Open',
            badgeCls: 'bx-badge',
            href: 'CAS-02_case-detail.html',
            mono: true,
          },
        ],
      },
    ];
    const q = (this.state.q || '').trim().toLowerCase();
    const qUp = q.toUpperCase();
    const isTok = this.isToken(qUp);
    const hit = isTok ? this.tokenHits(qUp) : null;
    let first = true;
    const mark = (it: any) => {
      const o = Object.assign({}, it, {
        pCls: it.mono ? 'bx-m' : '',
        bg: first ? 'var(--surface-2)' : 'transparent',
      });
      first = false;
      return o;
    };
    const groups = isTok
      ? hit
        ? [{ label: hit.kind, icon: icons.account, items: hit.rows.map(mark) }]
        : []
      : all
          .map((g) => ({
            label: g.g,
            icon: g.icon,
            items: g.items.filter((it) => !q || (it.primary + ' ' + it.name + ' ' + it.secondary).toLowerCase().includes(q)).map(mark),
          }))
          .filter((g) => g.items.length);
    const firstHref = groups.length ? groups[0].items[0].href : null;
    const opening = !!this.state.opening && !!hit;
    const tokNote = !hit
      ? ''
      : opening
        ? t('c.topbar.token-opening', 'Opening {account}…', { account: hit.rows[0].primary + ' · ' + hit.rows[0].name })
        : hit.rows.length > 1
          ? t('c.topbar.token-ip-many', '{n} accounts share this IP · Enter opens the first', { n: hit.rows.length })
          : t('c.topbar.token-found', 'Found by token · Enter opens the account');

    const alertsAll = [
      {
        sev: 'crit',
        title: 'Markout spike · XAUUSD',
        detail: '5 accounts, +$6.2/lot at 1s, shared /24 subnet',
        time: 18 + (tick % 40) + 's',
      },
      {
        sev: 'crit',
        title: 'XAUUSD exposure at 80% of limit',
        detail: 'Net short 84 lots against 105-lot limit',
        time: '1m',
      },
      {
        sev: 'warn',
        title: 'US CPI in 18 min',
        detail: '23 known news traders hold pending orders',
        time: '3m',
      },
      {
        sev: 'warn',
        title: 'Feed lag 1.8s · US30',
        detail: '11 fills inside lag window, est. cost $2,140',
        time: '7m',
      },
      {
        sev: 'info',
        title: 'Account 5021904 reclassified',
        detail: 'Scalper → Latency arbitrage',
        time: '12m',
      },
    ];
    const df = this.state.dFilter;
    const dItems = alertsAll
      .filter((a) => df === 'all' || a.sev === df)
      .map((a) => Object.assign({}, a, { dot: 'bx-sevdot bx-sevdot--' + a.sev }));
    const dFilters = [
      ['all', 'All', 5],
      ['crit', 'Critical', 2],
      ['warn', 'Warning', 2],
      ['info', 'Info', 1],
    ].map(([id, label, n]) => ({
      label,
      n,
      pressed: df === id ? 'true' : 'false',
      pick: () => this.setState({ dFilter: id }),
    }));

    const users = {
      owner: ['OS', 'Omar Siddiqui', 'Owner', 'omar@broker.com'],
      risk: ['RM', 'Rana Malik', 'Risk manager', 'rana@broker.com'],
      dealer: ['DM', 'Daniel Mensah', 'Dealer', 'daniel@broker.com'],
      launcher: ['LA', 'Launcher', 'Instance setup', 'launcher@broker.com'],
    };
    const u = users[role] || users.risk;
    const setTheme = (v) => {
      if (this.props.onTheme) this.props.onTheme(v);
      else this.setState({ theme: v });
    };
    const setDir = (v) => {
      if (this.props.onDir) this.props.onDir(v);
      else this.setState({ dir: v });
    };
    const alerts = this.props.alerts ?? 5;
    const base = {
      pillClass: pill.cls,
      dotClass: pill.dot,
      pillSub: pill.sub,
      pillSubColor: pill.subC,
      pillMetric: pill.metric,
      pillMetricColor: pill.metC,
      isLauncher: role === 'launcher',
      alerts,
      bellLabel: 'Alerts, ' + alerts + ' open',
      showPaused: collector === 'crit',
      pausedSecs: this.state.paused,
      lic,
      initials: u[0],
      userName: u[1],
      userRole: u[2],
      userEmail: u[3],
      userLabel: 'User menu for ' + u[1],
      menuOpen: this.state.menuOpen,
      menuExpanded: this.state.menuOpen ? 'true' : 'false',
      toggleMenu: () => this.setState({ menuOpen: !this.state.menuOpen, drawerOpen: false }),
      langs: [
        ['en', 'English', 'LTR', 'ltr'],
        ['fa', 'فارسی', 'RTL', 'rtl'],
        ['ar', 'العربية', 'RTL', 'rtl'],
      ].map(([code, label, d, dv]) => ({
        code,
        label,
        dir: d,
        checked: (code === 'en' ? dir === 'ltr' : dir === 'rtl' && code === (this.state.lang || 'fa')) ? 'true' : 'false',
        mark: (code === 'en' ? dir === 'ltr' : dir === 'rtl' && code === (this.state.lang || 'fa')) ? '✓' : '',
        pick: () => {
          this.setState({ lang: code, menuOpen: false });
          setDir(dv);
        },
      })),
      themes: [
        ['dark', 'Dark'],
        ['light', 'Light'],
      ].map(([id, label]) => ({
        label,
        checked: theme === id ? 'true' : 'false',
        mark: theme === id ? '✓' : '',
        pick: () => {
          this.setState({ menuOpen: false });
          setTheme(id);
        },
      })),
      soundOn: this.state.sound ? 'true' : 'false',
      toggleSound: () => this.setState({ sound: !this.state.sound }),
      searchOpen: this.state.searchOpen,
      q: this.state.q,
      qRef: this.qRef,
      groups,
      qUp,
      qFont: isTok ? 'var(--font-mono)' : 'var(--font-sans)',
      noResults: groups.length === 0 && !isTok,
      tokMiss: isTok && !hit,
      tokNote,
      opening,
      openSearch: () => this.setState({ searchOpen: true, menuOpen: false }),
      closeSearchBg: (e) => {
        if (e.target === e.currentTarget) {
          clearTimeout(this._open);
          this.setState({ searchOpen: false, opening: false });
        }
      },
      onQ: (e) => {
        clearTimeout(this._open);
        this.setState({ q: e.target.value, opening: false });
      },
      onQKey: (e) => {
        if (e.key === 'Escape') {
          clearTimeout(this._open);
          this.setState({ searchOpen: false, opening: false });
        }
        if (e.key === 'Enter' && firstHref) {
          try {
            window.location.href = route(firstHref) || firstHref;
          } catch (_) {
            /* sample navigation */
          }
        }
      },
      // Pasting a token that resolves to exactly one account opens it straight away.
      onQPaste: (e) => {
        let txt = '';
        try {
          txt = (e.clipboardData && e.clipboardData.getData('text')) || '';
        } catch (_) {
          txt = '';
        }
        const tk = txt.trim().toUpperCase();
        if (!this.isToken(tk)) return;
        e.preventDefault();
        const h = this.tokenHits(tk);
        const single = !!h && h.rows.length === 1;
        clearTimeout(this._open);
        this.setState({ q: tk, opening: single });
        if (single)
          this._open = setTimeout(() => {
            try {
              window.location.href = route(h.rows[0].href) || h.rows[0].href;
            } catch (_) {
              /* sample navigation */
            }
          }, 900);
      },
      drawerOpen: this.state.drawerOpen,
      drawerExpanded: this.state.drawerOpen ? 'true' : 'false',
      toggleDrawer: () => this.setState({ drawerOpen: !this.state.drawerOpen, menuOpen: false }),
      dFilters,
      dItems,
      serverLabel: 'MT5 · LIVE-01',
      pausedText: t(
        'c.topbar.paused-text',
        'Collector {loc} lost its connection to MT5 · {server}. Values on screen are frozen and marked stale until it reconnects.',
        { loc: 'LD4', server: 'LIVE-01' },
      ),
      drawerOpenN: 5,
      drawerCritN: 2,
      selIndex: -1,
      ackAll: undefined as any,
      signOut: undefined as any,
      itemClick: undefined as any,
    };
    return this.props.server ? Object.assign(base, this.liveVals()) : base;
  }

  /** Values from live data (app shell); the design's sample values stay for the standalone preview. */
  liveVals() {
    const p = this.props;
    const sv = p.server!;
    // connected false with no collector paired is setup, not an outage; idle is a closed market or a stopped feed
    const status =
      sv.status === 'crit' || (!sv.connected && sv.collector_status !== 'unpaired')
        ? 'crit'
        : sv.status === 'warn' || sv.status === 'idle' || sv.status === 'unknown'
          ? sv.status
          : 'ok';
    const tps = Math.round(sv.ticks_per_second || 0).toLocaleString('en-US');
    const latency =
      sv.latency_ms == null ? '—' : unit(sv.latency_ms < 10 ? Number(sv.latency_ms.toFixed(1)) : Math.round(sv.latency_ms), 'millisecond');
    const metric = latency + ' · ' + t('c.topbar.ticks-per-second', '{n} t/s', { n: tps });
    const pausedSecs = sv.last_tick_at ? Math.max(0, Math.round((Date.now() - Date.parse(sv.last_tick_at)) / 1000)) : 0;
    const pill = {
      ok: {
        cls: 'bx-pill',
        dot: 'bx-live bx-live--lg',
        sub: t('c.topbar.collector', 'Collector {loc}', {
          loc: sv.collector_location,
        }),
        subC: 'var(--muted)',
        metric,
        metC: 'var(--muted)',
      },
      warn: {
        cls: 'bx-pill bx-pill--warn',
        dot: 'bx-live bx-live--lg bx-live--warn',
        sub: t('c.topbar.collector-high-latency', 'Collector {loc} · high latency', { loc: sv.collector_location }),
        subC: 'var(--warn)',
        metric,
        metC: 'var(--warn)',
      },
      crit: {
        cls: 'bx-pill bx-pill--crit',
        dot: 'bx-live bx-live--lg bx-live--crit',
        sub: t('c.topbar.disconnected', 'Disconnected'),
        subC: 'var(--crit-text)',
        metric: t('c.topbar.last-tick', 'last tick {s}s', { s: pausedSecs }),
        metC: 'var(--crit-text)',
      },
      idle: {
        cls: 'bx-pill bx-pill--warn',
        dot: 'bx-live bx-live--lg bx-live--warn',
        sub: t('c.topbar.connected-no-ticks', 'Connected · no ticks'),
        subC: 'var(--warn)',
        metric: t('c.topbar.last-tick-ago', 'last tick {t}', { t: age(sv.last_tick_at) }),
        metC: 'var(--warn)',
      },
      unknown: {
        cls: 'bx-pill',
        dot: 'bx-live bx-live--lg bx-live--off',
        sub:
          sv.collector_status === 'unpaired'
            ? t('c.topbar.no-collector', 'No collector paired')
            : t('c.topbar.collector', 'Collector {loc}', { loc: sv.collector_location }),
        subC: 'var(--muted)',
        metric: '—',
        metC: 'var(--muted)',
      },
    }[status];
    const b = p.licenceBanner;
    const n = b?.days_left ?? 0;
    const liveLic: Record<string, any> = {
      expires_soon: {
        cls: 'bx-banner bx-banner--warn',
        title: t('c.topbar.lic-expiring', 'Licence expires in {n, plural, one {# day} other {# days}}.', { n }),
        text: t('c.topbar.lic-expiring-text', 'Renew by {date} to keep live monitoring, alerts and scheduled reports running.', {
          date: b?.date || '',
        }),
        cta: t('c.topbar.lic-enter-key', 'Enter licence key'),
        btnCls: 'bx-btn bx-btn--sm',
        icon: 'M12 4 2.5 20h19L12 4ZM12 10v4M12 17.5v.01',
      },
      expired_grace: {
        cls: 'bx-banner bx-banner--crit',
        title: t('c.topbar.lic-expired', 'Licence expired · read-only in {n, plural, one {# day} other {# days}}.', { n }),
        text: t('c.topbar.lic-expired-text', 'On {date} actions, alerts and scheduled reports stop. Live monitoring stays on until then.', {
          date: b?.date || '',
        }),
        cta: t('c.topbar.lic-renew', 'Renew licence'),
        btnCls: 'bx-btn bx-btn--sm bx-btn--danger',
        icon: 'M12 4 2.5 20h19L12 4ZM12 10v4M12 17.5v.01',
      },
      read_only: {
        cls: 'bx-banner bx-banner--crit',
        title: t('c.topbar.lic-readonly', 'Read-only mode.'),
        text: t(
          'c.topbar.lic-readonly-text',
          'The licence expired and its 7-day grace period has ended. Live data still shows; trading-server actions, alerts and scheduled reports are paused.',
        ),
        cta: t('c.topbar.lic-renew', 'Renew licence'),
        btnCls: 'bx-btn bx-btn--sm bx-btn--danger',
        icon: 'M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5zM12 15v2',
      },
      console_unreachable: {
        cls: 'bx-banner bx-banner--warn',
        title: t('c.topbar.lic-console', 'Dux FX console unreachable for {n, plural, one {# day} other {# days}}.', { n: n || 7 }),
        text: t(
          'c.topbar.lic-console-text',
          'Licence checks are running on the grace period. Allow outbound HTTPS to console.duxfx.com from this instance. Only admins see this.',
        ),
        cta: t('c.topbar.lic-instance', 'View instance status'),
        btnCls: 'bx-btn bx-btn--sm',
        icon: 'M12 4 2.5 20h19L12 4ZM12 10v4M12 17.5v.01',
      },
    };
    const lic = b && b.kind !== 'locked' && liveLic[b.kind] ? Object.assign({ show: true }, liveLic[b.kind]) : { show: false };
    const u = p.user || { name: '', email: '', role: '' };
    const lang = p.lang || 'en';
    const theme = p.theme || 'dark';
    const results: TopBarSearchGroup[] = this.state.results || [];
    let k = 0;
    const sel = this.state.sel || 0;
    // a client token: the account hits under the token's kind, "No account you can see has this token." when none
    const q = this.state.q || '';
    const qUp = q.trim().toUpperCase();
    const isTok = TOKEN_RE.test(qUp);
    const arrived = this.state.resQ === q;
    const tok: TopBarSearchToken | null = isTok && arrived ? this.state.token || null : null;
    const tokKind: Record<string, string> = {
      account: t('c.topbar.token-login', 'Login token'),
      email: t('c.topbar.token-email', 'Email token'),
      phone: t('c.topbar.token-phone', 'Phone token'),
      ip: t('c.topbar.token-ip', 'IP token'),
    };
    const matched: Record<string, string> = {
      email: t('c.topbar.matched-email', 'Matched by email'),
      phone: t('c.topbar.matched-phone', 'Matched by phone number'),
    };
    const shown = tok
      ? results.filter((g) => g.kind === 'account').map((g) => ({ ...g, label: tokKind[tok.kind] || g.label }))
      : isTok && !arrived
        ? []
        : results;
    const groups = shown
      .filter((g) => g.items.length)
      .map((g) => ({
        label: g.label,
        icon: SEARCH_ICONS[g.kind] || SEARCH_ICONS.account,
        items: g.items.map((it) => {
          const i = k++;
          const cut = it.label.indexOf(' · ');
          const pre = tok && matched[tok.kind] ? matched[tok.kind] + (it.sublabel ? ' · ' : '') : '';
          return {
            primary: cut > 0 ? it.label.slice(0, cut) : it.label,
            name: cut > 0 ? it.label.slice(cut + 3) : '',
            secondary: pre + (it.sublabel || ''),
            badge: it.badge || '',
            badgeCls: it.badgeCls || 'bx-badge',
            href: it.path,
            pCls: 'bx-m',
            bg: i === sel ? 'var(--surface-2)' : 'transparent',
            open: (e) => {
              e.preventDefault();
              this.go(it.path);
            },
          };
        }),
      }));
    const flat = groups.flatMap((g) => g.items);
    const opening = !!this.state.opening && !!tok?.found && flat.length === 1;
    const many: Record<string, string> = {
      ip: t('c.topbar.token-ip-many', '{n} accounts share this IP · Enter opens the first', { n: flat.length }),
      email: t('c.topbar.token-email-many', '{n} accounts share this email address · Enter opens the first', { n: flat.length }),
      phone: t('c.topbar.token-phone-many', '{n} accounts share this phone number · Enter opens the first', { n: flat.length }),
    };
    const tokNote =
      !tok?.found || !flat.length
        ? ''
        : opening
          ? t('c.topbar.token-opening', 'Opening {account}…', { account: flat[0].primary + (flat[0].name ? ' · ' + flat[0].name : '') })
          : flat.length > 1
            ? many[tok.kind] || t('c.topbar.token-many', '{n} accounts have this token · Enter opens the first', { n: flat.length })
            : t('c.topbar.token-found', 'Found by token · Enter opens the account');
    const df = this.state.dFilter;
    const sev = (x: string) => (x === 'critical' ? 'crit' : x === 'warning' ? 'warn' : 'info');
    const all = (p.alertItems || []).filter((a) => a.status === 'open');
    const nOf = (f: string) => all.filter((a) => f === 'all' || sev(a.severity) === f).length;
    return {
      pillClass: pill.cls,
      dotClass: pill.dot,
      pillSub: pill.sub,
      pillSubColor: pill.subC,
      pillMetric: pill.metric,
      pillMetricColor: pill.metC,
      serverLabel: 'MT5 · ' + (sv.name || '—'),
      showPaused: status === 'crit' || (!!p.stale && (status === 'ok' || status === 'warn')),
      pausedSecs,
      pausedText: t(
        'c.topbar.paused-text',
        'Collector {loc} lost its connection to MT5 · {server}. Values on screen are frozen and marked stale until it reconnects.',
        { loc: sv.collector_location, server: sv.name },
      ),
      lic,
      alerts: p.counts?.alerts_open ?? 0,
      bellLabel: t('c.topbar.bell', 'Alerts, {n} open', {
        n: p.counts?.alerts_open ?? 0,
      }),
      initials: initials(u.name || u.email),
      userName: u.name,
      userRole: u.role,
      userEmail: u.email,
      userLabel: t('c.topbar.user-menu', 'User menu for {name}', {
        name: u.name,
      }),
      langs: (p.languages || []).map((l) => ({
        code: l.code,
        label: l.native_name,
        dir: l.direction.toUpperCase(),
        checked: l.code === lang ? 'true' : 'false',
        mark: l.code === lang ? '✓' : '',
        pick: () => {
          this.setState({ menuOpen: false });
          p.onLang?.(l.code);
        },
      })),
      themes: [
        ['dark', t('c.topbar.dark', 'Dark')],
        ['light', t('c.topbar.light', 'Light')],
      ].map(([id, label]) => ({
        label,
        checked: theme === id ? 'true' : 'false',
        mark: theme === id ? '✓' : '',
        pick: () => {
          this.setState({ menuOpen: false });
          p.onTheme?.(id);
        },
      })),
      soundOn: p.sound ? 'true' : 'false',
      toggleSound: () => p.onSound?.(!p.sound),
      groups,
      qUp,
      qFont: isTok ? 'var(--font-mono)' : 'var(--font-sans)',
      noResults: !!q && groups.length === 0 && (!isTok || (arrived && !tok)),
      tokMiss: !!tok && !tok.found,
      tokNote,
      opening,
      openSearch: () => {
        this.setState({ searchOpen: true, menuOpen: false });
        this.runSearch('');
      },
      onQ: (e) => this.runSearch(e.target.value),
      // a pasted token is searched at once; one account found opens it
      onQPaste: (e) => {
        let txt = '';
        try {
          txt = e.clipboardData?.getData('text') || '';
        } catch {
          txt = '';
        }
        const tk = txt.trim().toUpperCase();
        if (!TOKEN_RE.test(tk)) return;
        e.preventDefault();
        this.runSearch(tk, { now: true, paste: true });
      },
      onQKey: (e) => {
        if (e.key === 'Escape') {
          clearTimeout(this._open);
          this.setState({ searchOpen: false, opening: false });
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.setState({ sel: Math.min(flat.length - 1, sel + 1) });
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.setState({ sel: Math.max(0, sel - 1) });
        } else if (e.key === 'Enter' && flat[sel]) {
          e.preventDefault();
          this.go(flat[sel].href);
        }
      },
      dFilters: [
        ['all', t('c.topbar.all', 'All')],
        ['crit', t('c.topbar.critical', 'Critical')],
        ['warn', t('c.topbar.warning', 'Warning')],
        ['info', t('c.topbar.info', 'Info')],
      ].map(([id, label]) => ({
        label,
        n: nOf(id),
        pressed: df === id ? 'true' : 'false',
        pick: () => this.setState({ dFilter: id }),
      })),
      dItems: all
        .filter((a) => df === 'all' || sev(a.severity) === df)
        .slice(0, 20)
        .map((a) => ({
          id: a.id,
          sev: sev(a.severity),
          dot: 'bx-sevdot bx-sevdot--' + sev(a.severity),
          title: tf(a, 'title'),
          detail: alertMessage(a),
          time: age(a.fired_at),
          late: !!a.late,
          href: '/alert-detail?id=' + encodeURIComponent(a.id),
        })),
      drawerOpenN: p.counts?.alerts_open ?? all.length,
      drawerCritN: p.counts?.alerts_critical ?? 0,
      ackAll: () => p.onAckAll?.(all.map((a) => a.id)),
      signOut: (e) => {
        e.preventDefault();
        this.setState({ menuOpen: false });
        p.onSignOut?.();
      },
      itemClick: (e) => {
        const a = (e.currentTarget as HTMLAnchorElement).getAttribute('href');
        if (a && p.onNavigate) {
          e.preventDefault();
          this.go(a);
        }
      },
    };
  }
  view(v: any) {
    return (
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          color: 'var(--ink)',
          fontSize: '13px',
          lineHeight: '19px',
        }}
      >
        <header className="bx-top">
          <a className="bx-brand" href="/dashboard" aria-label={t('c.topbar.brainux-dashboard', 'Brainux dashboard')}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              style={{ stroke: 'var(--gold)' }}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 4a4 4 0 0 0-4 4 4 4 0 0 0-3 6.5A4 4 0 0 0 9 20h3V4Z" />
              <path d="M12 4a4 4 0 0 1 4 4 4 4 0 0 1 3 6.5A4 4 0 0 1 15 20h-3" />
              <path d="M8.5 11h3M12.5 14h3" />
            </svg>
            <b>{t('c.topbar.brainux', 'Brainux')}</b>
            <span className="bx-by">{t('c.topbar.by-dux-fx', 'BY DUX FX')}</span>
          </a>
          <a
            className={v.pillClass}
            href="/system-health"
            title={t('c.topbar.open-collector-health', 'Open collector health')}
            style={{ textDecoration: 'none' }}
          >
            {' '}
            <span className={v.dotClass} /> <span>{ip(v.serverLabel)}</span> <span style={{ color: v.pillSubColor }}>{ip(v.pillSub)}</span>{' '}
            <span className="bx-m" style={{ color: v.pillMetricColor }}>
              {ip(v.pillMetric)}
            </span>{' '}
          </a>
          {v.isLauncher ? <span className="bx-badge bx-badge--gold">{t('c.topbar.launcher-account', 'Launcher account')}</span> : null}
          <div className="bx-spacer" />
          <button
            type="button"
            className="bx-topsearch"
            onClick={v.openSearch}
            aria-label={t('c.topbar.search-accounts-symbols-groups-and-cases', 'Search accounts, symbols, groups and cases')}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-4-4" />
            </svg>
            <span className="ph">{t('c.topbar.search-login-symbol-group-or-case', 'Search login, symbol, group or case')}</span>
            <span className="bx-kbd">{'⌘K'}</span>
          </button>
          <button type="button" className="bx-iconbtn" aria-label={v.bellLabel} aria-expanded={v.drawerExpanded} onClick={v.toggleDrawer}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4l2-2Z" />
              <path d="M10 21h4" />
            </svg>
            {v.alerts ? <span className="bx-count">{ip(v.alerts)}</span> : null}
          </button>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className="bx-avatar"
              aria-label={v.userLabel}
              aria-haspopup="menu"
              aria-expanded={v.menuExpanded}
              onClick={v.toggleMenu}
            >
              {ip(v.initials)}
            </button>{' '}
            {v.menuOpen ? (
              <div className="bx-menu" role="menu" style={{ top: '42px', insetInlineEnd: '0', width: '260px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 10px 10px',
                  }}
                >
                  <span className="bx-avatar">{ip(v.initials)}</span>
                  <div style={{ minWidth: '0' }}>
                    <div style={{ fontWeight: '500', color: 'var(--ink)' }}>{ip(v.userName)}</div>
                    <div className="bx-cap">
                      {ip(v.userRole)} {'·'} {ip(v.userEmail)}
                    </div>
                  </div>
                </div>
                <div className="bx-menu-sep" />
                <a className="bx-menu-it" role="menuitem" href="/profile">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                  </svg>
                  {t('c.topbar.my-profile', 'My profile')}
                </a>
                <div className="bx-menu-h">{t('c.topbar.language', 'Language')}</div>
                {each(v.langs, (l, $lI) => (
                  <button
                    key={$lI}
                    type="button"
                    className="bx-menu-it"
                    role="menuitemradio"
                    aria-checked={l?.checked}
                    onClick={l?.pick}
                    lang={l?.code}
                  >
                    <span style={{ width: '16px', textAlign: 'center' }} aria-hidden="true">
                      {ip(l?.mark)}
                    </span>
                    {ip(l?.label)}
                    <span className="r">{ip(l?.dir)}</span>
                  </button>
                ))}
                <div className="bx-menu-h">{t('c.topbar.theme', 'Theme')}</div>
                {each(v.themes, (th, $thI) => (
                  <button
                    key={$thI}
                    type="button"
                    className="bx-menu-it"
                    role="menuitemradio"
                    aria-checked={th?.checked}
                    onClick={th?.pick}
                  >
                    <span style={{ width: '16px', textAlign: 'center' }} aria-hidden="true">
                      {ip(th?.mark)}
                    </span>
                    {ip(th?.label)}
                  </button>
                ))}
                <div className="bx-menu-sep" />
                <div className="bx-menu-it" style={{ cursor: 'default' }}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 10v4h4l5 4V6L8 10H4Z" />
                    <path d="M16 9a4 4 0 0 1 0 6" />
                  </svg>
                  {t('c.topbar.critical-alert-sound', 'Critical alert sound')}
                  <button
                    type="button"
                    className="bx-switch"
                    role="switch"
                    aria-checked={v.soundOn}
                    aria-label={t('c.topbar.critical-alert-sound', 'Critical alert sound')}
                    onClick={v.toggleSound}
                    style={{ marginInlineStart: 'auto' }}
                  />
                </div>
                <div className="bx-menu-sep" />
                <a className="bx-menu-it" role="menuitem" href="/login" onClick={v.signOut}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9 4H5v16h4" />
                    <path d="m14 8 4 4-4 4M18 12H9" />
                  </svg>
                  {t('c.topbar.sign-out', 'Sign out')}
                </a>
              </div>
            ) : null}
          </div>
        </header>
        {v.showPaused ? (
          <div className="bx-banner bx-banner--crit" role="status">
            <span className="bx-banner-ic">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M5 12.5a10 10 0 0 1 4-2.3M19 12.5a10 10 0 0 0-2.7-1.9M8.5 16a5 5 0 0 1 7 0M12 19.5v.01M3 3l18 18" />
              </svg>
            </span>
            <span>
              <b>
                {rich((m) =>
                  t('c.topbar.live-data-paused', 'Live data paused · last tick {s}s ago.', {
                    s: m(<span className="bx-m">{ip(v.pausedSecs)}</span>),
                  }),
                )}
              </b>{' '}
              {ip(v.pausedText)}
            </span>
            <span className="bx-banner-x">
              <a className="bx-btn bx-btn--sm bx-btn--danger" href="/system-health">
                {t('c.topbar.open-system-health', 'Open system health')}
              </a>
            </span>
          </div>
        ) : null}{' '}
        {v.lic?.show ? (
          <div className={v.lic?.cls} role="status">
            <span className="bx-banner-ic">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d={v.lic?.icon} />
              </svg>
            </span>{' '}
            <span>
              <b>{ip(v.lic?.title)}</b> {ip(v.lic?.text)}
            </span>{' '}
            <span className="bx-banner-x">
              <a className={v.lic?.btnCls} href="/instance-licence">
                {ip(v.lic?.cta)}
              </a>
            </span>
          </div>
        ) : null}{' '}
        {v.searchOpen ? (
          <div className="bx-scrim bx-scrim--top" onClick={v.closeSearchBg}>
            <div
              role="dialog"
              aria-label={t('c.topbar.search', 'Search')}
              aria-modal="true"
              style={{
                width: 'min(640px, 100%)',
                background: 'var(--surface-1)',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-2xl)',
                boxShadow: 'var(--shadow-pop)',
                overflow: 'hidden',
                animation: 'bx-in-top .16s ease-out',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ stroke: 'var(--muted)' }}
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-4-4" />
                </svg>
                <input
                  type="text"
                  ref={v.qRef}
                  value={v.q}
                  onChange={v.onQ}
                  onKeyDown={v.onQKey}
                  onPaste={v.onQPaste}
                  placeholder={t(
                    'c.topbar.search-login-name-symbol-group-or-token',
                    'Search login, name, symbol, group or case, or paste a client token',
                  )}
                  aria-label={t('c.topbar.search-query', 'Search query')}
                  dir="auto"
                  style={{
                    flex: '1',
                    minWidth: '0',
                    background: 'transparent',
                    border: '0',
                    outline: 'none',
                    color: 'var(--ink)',
                    font: '15px/22px ' + (v.qFont || 'var(--font-sans)'),
                  }}
                />
                <span className="bx-kbd">{t('c.topbar.esc', 'Esc')}</span>
              </div>
              <div
                style={{
                  maxHeight: '420px',
                  overflow: 'auto',
                  padding: '6px 8px 10px',
                }}
              >
                {each(v.groups, (g, $gI) => (
                  <Fragment key={$gI}>
                    <div style={{ padding: '10px 8px 4px' }} className="bx-eb">
                      {ip(g?.label)}
                    </div>
                    {each(g?.items, (it, $itI) => (
                      <a
                        key={$itI}
                        href={route(it?.href)}
                        onClick={it?.open}
                        className="bx-menu-it"
                        style={{
                          padding: '8px',
                          color: 'var(--ink-2)',
                          background: it?.bg,
                        }}
                      >
                        <span
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--surface-2)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--muted)',
                            flex: 'none',
                          }}
                        >
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d={g?.icon} />
                          </svg>
                        </span>
                        <span
                          style={{
                            minWidth: '0',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <span>
                            <span className={it?.pCls} style={{ color: 'var(--ink)' }}>
                              {ip(it?.primary)}
                            </span>
                            <span style={{ color: 'var(--muted)' }}>
                              {' '}
                              {'·'} {ip(it?.name)}
                            </span>
                          </span>
                          <span className="bx-cap">{ip(it?.secondary)}</span>
                        </span>
                        {it?.badge ? (
                          <span className={it?.badgeCls} style={{ marginInlineStart: 'auto' }}>
                            {ip(it?.badge)}
                          </span>
                        ) : null}
                      </a>
                    ))}
                  </Fragment>
                ))}{' '}
                {v.tokNote ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 8px 2px',
                      fontSize: '12px',
                      lineHeight: '17px',
                      color: 'var(--muted)',
                    }}
                  >
                    {v.opening ? <span className="bx-spinner" style={{ width: '12px', height: '12px' }} aria-hidden="true" /> : null}
                    <span role="status">{ip(v.tokNote)}</span>
                  </div>
                ) : null}{' '}
                {v.noResults ? (
                  <div className="bx-state" style={{ padding: '28px 16px' }}>
                    <span className="bx-state-t">{t('c.topbar.no-results-for', 'No results for “{q}”', { q: v.q })}</span>
                    <span className="bx-state-d">
                      {t(
                        'c.topbar.search-matches-mt5-logins-client-names-tokens',
                        'Search matches MT5 logins, client names, canonical symbols and suffixes, group paths, case numbers and client tokens.',
                      )}
                    </span>
                  </div>
                ) : null}{' '}
                {v.tokMiss ? (
                  <div className="bx-state" style={{ padding: '28px 16px' }}>
                    <span className="bx-state-ic">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21c0-4.4 3.6-8 8-8 1.4 0 2.7.4 3.9 1M16 17l5 5M21 17l-5 5" />
                      </svg>
                    </span>
                    <span className="bx-state-t">{t('c.topbar.no-account-you-can-see-has', 'No account you can see has this token.')}</span>
                    <span className="bx-state-d">
                      <span className="bx-m bx-ink2" dir="ltr">
                        {ip(v.qUp)}
                      </span>{' '}
                      {t(
                        'c.topbar.tokens-come-from-ask-brainux-and',
                        '· Tokens come from Ask Brainux and from AI apps connected to this Brainux. Check that the whole token was copied.',
                      )}
                    </span>
                  </div>
                ) : null}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '14px',
                  padding: '9px 16px',
                  borderTop: '1px solid var(--border)',
                  fontSize: '11px',
                  color: 'var(--muted)',
                }}
              >
                <span>
                  <span className="bx-kbd">{'↑'}</span> <span className="bx-kbd">{'↓'}</span> {t('c.topbar.to-move', 'to move')}
                </span>
                <span>
                  <span className="bx-kbd">{t('c.topbar.enter', 'Enter')}</span> {t('c.topbar.to-open', 'to open')}
                </span>
                <span>
                  <span className="bx-kbd">{t('c.topbar.esc', 'Esc')}</span> {t('c.topbar.to-close', 'to close')}
                </span>
              </div>
            </div>
          </div>
        ) : null}{' '}
        {v.drawerOpen ? (
          <>
            <div className="bx-drawer-scrim" onClick={v.toggleDrawer} />
            <aside className="bx-drawer" aria-label={t('c.topbar.latest-alerts', 'Latest alerts')}>
              <div className="bx-drawer-h">
                <div style={{ flex: '1', minWidth: '0' }}>
                  <h2 className="bx-t">{t('c.topbar.alerts', 'Alerts')}</h2>
                  <p className="bx-c">
                    {rich((m) =>
                      t('c.topbar.open-critical-updates-live', '{open} open · {critical} critical · updates live', {
                        open: m(<span className="bx-m">{ip(v.drawerOpenN)}</span>),
                        critical: m(<span className="bx-m bx-crit">{ip(v.drawerCritN)}</span>),
                      }),
                    )}
                  </p>
                </div>
                <button type="button" className="bx-x" aria-label={t('c.topbar.close-alerts', 'Close alerts')} onClick={v.toggleDrawer}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>
              <div style={{ padding: '12px 18px 0' }}>
                <div className="bx-seg" role="group" aria-label={t('c.topbar.severity', 'Severity')}>
                  {each(v.dFilters, (f, $fI) => (
                    <button key={$fI} type="button" aria-pressed={f?.pressed} onClick={f?.pick}>
                      {ip(f?.label)}{' '}
                      <span className="bx-m" style={{ opacity: '.8' }}>
                        {ip(f?.n)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="bx-drawer-b" style={{ gap: '0', paddingTop: '8px' }}>
                <div className="bx-feed">
                  {each(v.dItems, (a, $aI) => (
                    <a
                      key={a?.id ?? $aI}
                      className="bx-feed-it"
                      href={a?.href ?? '/alert-detail'}
                      onClick={v.itemClick}
                      style={{ paddingInline: '6px', marginInline: '-6px' }}
                    >
                      <span className={a?.dot} />
                      <span className="bx-feed-b">
                        <span className="bx-feed-l1">
                          <b>{ip(a?.title)}</b>
                          {a?.late ? (
                            <span
                              className="bx-badge bx-badge--warn"
                              title={t('c.topbar.late-title', 'The event reached Brainux late, so nobody was paged.')}
                            >
                              {t('c.topbar.late', 'Late')}
                            </span>
                          ) : null}
                          <span className="bx-feed-tm">{ip(a?.time)}</span>
                        </span>
                        <span className="bx-feed-d" style={{ display: 'block' }}>
                          {ip(a?.detail)}
                        </span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>
              <div className="bx-drawer-f" style={{ justifyContent: 'space-between' }}>
                <button type="button" className="bx-btn bx-btn--sm" onClick={v.ackAll} disabled={v.ackAll && !v.drawerOpenN}>
                  {t('c.topbar.acknowledge-all', 'Acknowledge all')}
                </button>
                <a className="bx-btn bx-btn--sm bx-btn--primary" href="/alerts" onClick={v.itemClick}>
                  {t('c.topbar.open-alerts', 'Open Alerts')}
                </a>
              </div>
            </aside>
          </>
        ) : null}
      </div>
    );
  }
}
